import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import {
  crearCuenta,
  entrar,
  loDeEsteDispositivo,
  MINIMO_CONTRASENA,
  nuevoCodigo,
  pedirCodigoSiFalta,
  recuperar,
  type LoLocal,
  type ResultadoCuenta,
  type Sesion,
} from '../store/cuenta';
import { useAcceso } from '../store/acceso';
import { db, wipeLocal } from '../store/db';
import { updateSettings } from '../store/mutations';
import { sincronizarAhora } from '../store/sync';
import { Segmented } from '../ui/primitivas';
import { resumenDeLoLocal } from './acceso-texto';
import { CampoContrasena } from '../ui/contrasena';
import {
  ErrorEnLinea,
  Firma,
  SimboloQueSeNivela,
  sincronizoDesde,
  useEstadoSync,
  useGesto,
} from './plano';

type Modo = 'crear' | 'entrar' | 'recuperar';
type Rumbo = 'adelante' | 'atras';

/** El orden de los caminos decide hacia dónde se desliza el cambio. */
const ORDEN: Record<Modo, number> = { crear: 0, entrar: 1, recuperar: 2 };

/**
 * LA PUERTA · la única pantalla sin cuenta (spec «cuenta obligatoria»).
 *
 * Tres caminos y una garantía. Crear la cuenta, entrar con la que ya tienes o, si
 * olvidaste la contraseña, ponerle una nueva con el código de recuperación. Y si el
 * dispositivo ya guarda algo de antes de que la cuenta fuera obligatoria, la puerta
 * lo dice primero: crear la cuenta lo sube tal cual, sin perder ni duplicar nada.
 *
 * Mientras trabaja (crea, entra, baja lo tuyo) la sostiene `accediendo`: la cuenta
 * queda vinculada antes de que termine, y sin eso la app se abriría a mitad.
 */
export function Acceso(): ReactElement {
  const [modo, setModo] = useState<Modo>('crear');
  const [rumbo, setRumbo] = useState<Rumbo>('adelante');
  const [nombre, setNombre] = useState('');
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [otraCuenta, setOtraCuenta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [inicio, setInicio] = useState(0);
  const [local, setLocal] = useState<LoLocal | null>(null);
  const [enLinea, setEnLinea] = useState(() => navigator.onLine);
  const { setAccediendo, mostrarCodigo } = useAcceso();
  const estado = useEstadoSync();
  const [fase, iniciarGesto] = useGesto();
  const ids = useId();

  useEffect(() => {
    let vivo = true;
    void loDeEsteDispositivo().then((l) => vivo && setLocal(l));
    // Si este dispositivo ya tuvo cuenta (cerró sesión), lo probable es volver a entrar,
    // no crear otra: se abre en «Ya tengo cuenta».
    void db.meta.get('ultimaCuenta').then((fila) => {
      if (vivo && typeof fila?.value === 'string' && fila.value.length > 0) setModo('entrar');
    });
    const alCambiar = () => setEnLinea(navigator.onLine);
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    return () => {
      vivo = false;
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
    };
  }, []);

  const loTuyo = resumenDeLoLocal(local);
  const bajado = sesion !== null && fase === 'hecho' && sincronizoDesde(estado, inicio);
  const atascado =
    sesion !== null &&
    fase === 'hecho' &&
    (estado.estado === 'sin-red' || estado.estado === 'error');

  /**
   * Al terminar de bajar lo de la cuenta: con algo dentro, la app abre con ello; vacía,
   * sigue el alta. Lo de este dispositivo ya subió al vincular.
   */
  useEffect(() => {
    if (!bajado || !sesion) return;
    let vivo = true;
    void (async () => {
      const [areas, habitos] = await Promise.all([db.lifeAreas.count(), db.habits.count()]);
      if (!vivo) return;
      if (areas + habitos > 0) {
        await updateSettings({
          ...(sesion.nombre ? { displayName: sesion.nombre } : {}),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          onboardedAt: new Date().toISOString(),
        });
      }
      setAccediendo(false);
    })();
    return () => {
      vivo = false;
    };
  }, [bajado, sesion, setAccediendo]);

  const cambiarModo = (siguiente: Modo) => {
    setRumbo(ORDEN[siguiente] < ORDEN[modo] ? 'atras' : 'adelante');
    setModo(siguiente);
    setError(null);
    setOtraCuenta(false);
    setPassword('');
  };

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    setOtraCuenta(false);
    if (modo !== 'entrar' && password.length < MINIMO_CONTRASENA) {
      setError(`La contraseña necesita al menos ${MINIMO_CONTRASENA} caracteres.`);
      return;
    }
    setEnviando(true);
    setAccediendo(true);
    setInicio(Date.now());

    if (modo === 'crear') {
      const resultado = await crearCuenta({ usuario, password, nombre });
      setEnviando(false);
      if (!resultado.ok) {
        setAccediendo(false);
        setError(resultado.mensaje);
        setOtraCuenta(resultado.otraCuenta === true);
        return;
      }
      if (nombre.trim()) await updateSettings({ displayName: nombre.trim() });
      try {
        mostrarCodigo(await nuevoCodigo());
      } catch {
        // Si falla justo aquí, Ajustes lo genera más tarde; la cuenta ya existe.
      }
      // Cuenta nueva: lo de este dispositivo ya está subiendo; si no había nada, el alta.
      setAccediendo(false);
      return;
    }

    // Recuperar devuelve un código nuevo (el usado ya no sirve); entrar, ninguno.
    let codigoNuevo: string | null = null;
    let resultado: ResultadoCuenta;
    if (modo === 'entrar') {
      resultado = await entrar({ identificador: usuario, password });
    } else {
      const recuperado = await recuperar({ usuario, codigo, password });
      if (recuperado.ok) codigoNuevo = recuperado.codigo;
      resultado = recuperado;
    }
    setEnviando(false);
    if (!resultado.ok) {
      setAccediendo(false);
      setError(resultado.mensaje);
      setOtraCuenta(resultado.otraCuenta === true);
      return;
    }
    setPassword('');
    setCodigo('');
    setSesion(resultado.sesion);
    iniciarGesto();
    if (codigoNuevo) mostrarCodigo(codigoNuevo);
    else void pedirCodigoSiFalta();
  };

  const borrarEsteDispositivo = async () => {
    // La sesión de la otra cuenta ya se cerró al detectarla: borrar no la resucita.
    await wipeLocal();
    window.location.reload();
  };

  let titular = 'Un hábito no es una tarea.';
  let bajada = 'Es la evidencia de que eres quien dices ser.';
  let contexto = 'selflevel · self-leveling';

  if (sesion) {
    contexto = 'Entrando';
    if (atascado) {
      titular = 'No se pudo traer lo tuyo.';
      bajada =
        estado.estado === 'sin-red'
          ? 'Sin conexión. Tu cuenta quedó vinculada: en cuanto vuelva la red, lo tuyo baja solo.'
          : 'El servidor no responde. Tu cuenta quedó vinculada y se reintentará solo.';
    } else if (bajado) {
      titular = 'Ya está aquí.';
      bajada = 'Lo de tu cuenta, en este dispositivo. Abriendo la app…';
    } else {
      titular = 'Bajando lo tuyo.';
      bajada = 'Hábitos, ritual, diario e hitos, de tu cuenta a este dispositivo.';
    }
  } else if (loTuyo && modo === 'crear') {
    titular = 'Crea tu cuenta para no perder lo tuyo.';
    bajada = `En este dispositivo: ${loTuyo}. Con la cuenta sube tal cual y lo recuperas en cualquier móvil.`;
  } else if (modo === 'entrar') {
    titular = 'Lo tuyo, en este dispositivo.';
    bajada = 'Entra con tu cuenta y baja lo que ya tienes.';
  } else if (modo === 'recuperar') {
    titular = 'Vuelve a entrar.';
    bajada =
      'Con tu usuario y el código de recuperación que guardaste, pones una contraseña nueva.';
  }

  return (
    <main className="sinc sinc--bienvenida">
      <div className="sinc__cuerpo">
        <section className="sinc__lienzo">
          <div className="sinc__intro">
            <div className="sinc__contexto">
              <span className="t-caption t-dim sinc__contexto-titulo">
                {sesion ? (
                  <span className="sinc__punto" data-punto="info" aria-hidden="true" />
                ) : null}
                {contexto}
              </span>
            </div>
            <SimboloQueSeNivela fase={sesion ? fase : 'quieto'} />
            {/* La `key` es el titular: cada cambio de camino lo vuelve a montar y entra
                deslizándose en el sentido del cambio, como los pasos del ritual. */}
            <div key={titular} className="sinc__frase acceso-cambia" data-rumbo={rumbo}>
              <h1 className="sinc__titular" aria-live="polite">
                {titular}
              </h1>
              <p className="sinc__bajada">{bajada}</p>
            </div>
          </div>
          <Firma className="sinc__firma--escritorio" />
        </section>

        <svg className="sinc__corte sinc__corte--escritorio" aria-hidden="true">
          <line x1="200" y1="0" x2="0" y2="100%" />
        </svg>

        <section className="sinc__plano">
          <svg className="sinc__corte sinc__corte--movil" aria-hidden="true">
            <line x1="0" y1="0.5" x2="100%" y2="104.5" />
          </svg>
          <div className="sinc__plano-contenido">
            {sesion ? (
              <div className="sinc__garantia">
                <span className="t-sm t-dim">
                  Lo de este dispositivo y lo de tu cuenta se juntan sin duplicarse.
                </span>
                {atascado ? (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      setInicio(Date.now());
                      void sincronizarAhora();
                    }}
                  >
                    Volver a intentarlo
                  </button>
                ) : null}
              </div>
            ) : (
              <Formulario
                ids={ids}
                modo={modo}
                rumbo={rumbo}
                nombre={nombre}
                usuario={usuario}
                password={password}
                codigo={codigo}
                error={error}
                otraCuenta={otraCuenta}
                enviando={enviando}
                enLinea={enLinea}
                onModo={cambiarModo}
                onNombre={setNombre}
                onUsuario={setUsuario}
                onPassword={setPassword}
                onCodigo={setCodigo}
                onEnviar={(e) => void enviar(e)}
                onBorrar={() => void borrarEsteDispositivo()}
              />
            )}
            <Firma className="sinc__firma--movil" />
          </div>
        </section>
      </div>
    </main>
  );
}

/** El formulario de la puerta: los mismos campos cambian según el camino elegido. */
function Formulario(p: {
  ids: string;
  modo: Modo;
  rumbo: Rumbo;
  nombre: string;
  usuario: string;
  password: string;
  codigo: string;
  error: string | null;
  otraCuenta: boolean;
  enviando: boolean;
  enLinea: boolean;
  onModo: (modo: Modo) => void;
  onNombre: (v: string) => void;
  onUsuario: (v: string) => void;
  onPassword: (v: string) => void;
  onCodigo: (v: string) => void;
  onEnviar: (e: FormEvent) => void;
  onBorrar: () => void;
}): ReactElement {
  const { ids, modo, enviando } = p;
  const textoBoton =
    modo === 'crear' ? 'Crear cuenta' : modo === 'entrar' ? 'Entrar' : 'Poner contraseña nueva';

  return (
    <>
      <div className="sinc__garantia">
        <strong className="t-body-strong">
          {modo === 'recuperar'
            ? 'El código lo guardaste al crear la cuenta.'
            : 'Tu cuenta guarda lo tuyo aunque cambies de móvil.'}
        </strong>
        <span className="t-sm t-dim">
          {p.enLinea
            ? 'Ya dentro, la app funciona también sin conexión.'
            : 'Ahora no hay conexión: para crear la cuenta o entrar hace falta internet.'}
        </span>
      </div>

      <Segmented
        block
        label="¿Tienes cuenta?"
        value={modo === 'recuperar' ? 'entrar' : modo}
        onChange={(v) => p.onModo(v)}
        options={[
          { value: 'crear', label: 'Crear cuenta' },
          { value: 'entrar', label: 'Ya tengo cuenta' },
        ]}
      />

      <form
        key={modo}
        className="sinc__form acceso-cambia acceso-cambia--detras"
        data-rumbo={p.rumbo}
        noValidate
        onSubmit={p.onEnviar}
      >
        {modo === 'crear' ? (
          <div className="field">
            <div className="sinc__etiqueta">
              <label htmlFor={`${ids}-nombre`}>¿Cómo te llamamos?</label>
              <span className="t-sm t-dim">Opcional</span>
            </div>
            <input
              id={`${ids}-nombre`}
              className="input"
              autoComplete="given-name"
              value={p.nombre}
              disabled={enviando}
              onChange={(e) => p.onNombre(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor={`${ids}-usuario`}>
            {modo === 'entrar' ? 'Usuario o correo' : 'Usuario'}
          </label>
          <input
            id={`${ids}-usuario`}
            className="input"
            required
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={p.usuario}
            disabled={enviando}
            onChange={(e) => p.onUsuario(e.target.value)}
          />
          {modo === 'crear' ? (
            <span className="t-sm t-dim">De 3 a 24: letras, números, punto o guion bajo.</span>
          ) : null}
        </div>

        {modo === 'recuperar' ? (
          <div className="field">
            <label htmlFor={`${ids}-codigo`}>Código de recuperación</label>
            <input
              id={`${ids}-codigo`}
              className="input"
              required
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={p.codigo}
              disabled={enviando}
              onChange={(e) => p.onCodigo(e.target.value)}
            />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor={`${ids}-pass`}>
            {modo === 'recuperar' ? 'Contraseña nueva' : 'Contraseña'}
          </label>
          <CampoContrasena
            id={`${ids}-pass`}
            required
            autoComplete={modo === 'entrar' ? 'current-password' : 'new-password'}
            minLength={modo === 'entrar' ? undefined : MINIMO_CONTRASENA}
            value={p.password}
            disabled={enviando}
            onChange={(e) => p.onPassword(e.target.value)}
          />
          {modo !== 'entrar' ? (
            <span className="t-sm t-dim">Al menos {MINIMO_CONTRASENA} caracteres.</span>
          ) : null}
        </div>

        {p.error ? <ErrorEnLinea mensaje={p.error} /> : null}
        {p.otraCuenta ? (
          <button className="btn btn--block" type="button" onClick={p.onBorrar}>
            Borrar los datos de este dispositivo
          </button>
        ) : null}

        <button
          className="btn btn--primary sinc__enviar"
          type="submit"
          disabled={enviando || !p.enLinea}
          aria-busy={enviando}
        >
          {enviando ? 'Un momento…' : textoBoton}
        </button>

        {modo === 'entrar' ? (
          <button
            className="btn btn--bare sinc__cierre"
            type="button"
            onClick={() => p.onModo('recuperar')}
          >
            ¿Olvidaste tu contraseña?
          </button>
        ) : null}
        {modo === 'recuperar' ? (
          <button
            className="btn btn--bare sinc__cierre"
            type="button"
            onClick={() => p.onModo('entrar')}
          >
            Entrar con contraseña
          </button>
        ) : null}
      </form>
    </>
  );
}
